// Treat '', undefined, and 'transparent' as equivalent empty values;
// also compare hex colors case-insensitively.
function colorsMatch(a, b) {
  const empty = v => v === '' || v === undefined || v === 'transparent';
  if (empty(a) && empty(b)) return true;
  if (typeof a === 'string' && typeof b === 'string') {
    return a.toLowerCase() === b.toLowerCase();
  }
  return a === b;
}

/**
 * Computes the diff between current values and theme values
 * Only returns properties that have actually changed
 */
export const computeThemeOverrides = (currentValues, themeValues) => {
  const diff = {};

  const compareValues = (current, theme, path = []) => {
    if (current === undefined || current === null) {
      return undefined;
    }

    // Note: we intentionally do NOT early-return when theme is undefined/null.
    // The comparison logic below has special cases for default values (empty
    // strings, fontSize=0, fontWeight=0, enabled=true) that must run even
    // when the theme has no value for this path.

    if (typeof current === 'object' && !Array.isArray(current)) {
      if (current.light !== undefined || current.dark !== undefined) {
        const themeColor = theme || { light: '', dark: '' };
        const colorDiff = {};
        let hasColorChanges = false;

        if (!colorsMatch(current.light, themeColor.light)) {
          colorDiff.light = current.light;
          hasColorChanges = true;
        }

        if (!colorsMatch(current.dark, themeColor.dark)) {
          colorDiff.dark = current.dark;
          hasColorChanges = true;
        }

        return hasColorChanges ? colorDiff : undefined;
      }

      const objDiff = {};
      let hasChanges = false;

      Object.keys(current).forEach(key => {
        const result = compareValues(current[key], theme ? theme[key] : undefined, [...path, key]);
        if (result !== undefined) {
          objDiff[key] = result;
          hasChanges = true;
        }
      });

      return hasChanges ? objDiff : undefined;
    }

    if (current !== theme) {
      if (
        (current === '' && (theme === '' || theme === undefined)) ||
        (current === 0 &&
          path[path.length - 1] === 'fontSize' &&
          (theme === 0 || theme === undefined)) ||
        (current === 0 &&
          path[path.length - 1] === 'fontWeight' &&
          (theme === 0 || theme === undefined)) ||
        // `enabled: true` normally equals a toggle's default, so it's stripped from
        // the diff. poweredByCheckoutPage is the exception: it has no default (unset
        // means "inherit the seller-level branding global" — see resolveBadge), so an
        // explicit `true` is a real override and must be persisted.
        (current === true &&
          path[path.length - 1] === 'enabled' &&
          path[path.length - 2] !== 'poweredByCheckoutPage' &&
          (theme === true || theme === undefined))
      ) {
        return undefined;
      }

      return current;
    }

    return undefined;
  };

  if (currentValues.variables && themeValues.variables) {
    const variablesDiff = compareValues(currentValues.variables, themeValues.variables);

    if (variablesDiff && Object.keys(variablesDiff).length > 0) {
      diff.variables = variablesDiff;
    }
  }

  diff.enabled = currentValues.enabled !== false;

  return diff;
};
